import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT, placementSize } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { SceneTransition } from '../ui/SceneTransition.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite } from '../ui/NatalkaSprite.js';
import { AnimalsController } from '../ui/AnimalsController.js';
import { openAnimalActions } from '../ui/animalActions.js';
import { PlacementHud } from '../ui/PlacementHud.js';
import { attachPlacementMenu } from '../ui/placementInteraction.js';
import { placementTexture } from '../ui/seating.js';
import { sizePlacement, applyPlacementFlip } from '../ui/placementSizing.js';
import { attachClockHand } from '../ui/clockHand.js';
import { depthSortScene } from '../ui/depthSort.js';
import { fitCharacter } from '../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from './roomPerspective.mjs';

import { reportLocation } from '../ui/locationTracker.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachSceneEditMode } from '../ui/SceneEditMode.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { FurnitureSeats, placementPairs } from '../ui/FurnitureSeats.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { preloader } from './PreloadScene.js';
import { renderBackground } from '../render/world.js';
import { iconText } from '../ui/iconText.js';
import { homeArrival } from './homeLayout.mjs';

const SCENE_KEY = 'kitchen';

export class KitchenScene extends Phaser.Scene {
  constructor() { super(SCENE.KITCHEN); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    this._spawn = { x: data?.spawnX ?? GAME_WIDTH / 2, y: data?.spawnY ?? GAME_HEIGHT - 240 };
    const bg = this.textures.exists('bg_kitchen') ? 'bg_kitchen' : 'bg_home';
    renderBackground(this, bg);

    iconText(this, GAME_WIDTH / 2, 80, '{pan} Kuchnia', {
      fontFamily: 'system-ui', fontSize: '32px', color: '#fff', fontStyle: 'bold',
      backgroundColor: '#000000aa', padding: { x: 12, y: 6 },
    }).setOrigin(0.5);

    this.perspective = ROOM_PERSPECTIVE.kitchen;   // smaller and slower towards the back wall
    this.natalka = new NatalkaSprite(this, this._spawn.x, this._spawn.y);
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    // Depth is y-sorted every frame (depthSortScene) so you can stand behind furniture.
    this.walker = new WalkController(this, this.natalka, { onBroadcast: (p) => realtime.move(p) });
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });

    this.animals = new AnimalsController(this, {
      sceneId: SCENE_KEY,
      ownerId: window.__myId ?? null,
      getPlayers: () => {
        const players = [];
        if (window.__myId) players.push({ id: window.__myId, x: this.natalka.x, y: this.natalka.y });
        if (this._presence) for (const [id, av] of this._presence.avatars) players.push({ id, x: av.sprite.x, y: av.sprite.y });
        return players;
      },
    });
    this.events.on('shutdown', () => this.animals.destroy());

    this._placementSprites = new Map();
    this._renderPlacementsAndAnimals();
    this.furnitureSeats = new FurnitureSeats(this, {
      natalka: this.natalka, walker: this.walker,
      getPlacements: () => placementPairs(this._placementSprites.values()),
      getPeers: () => this._presence?.avatars.values() ?? [],
    });

    this.transition = new SceneTransition(this);
    this.transition.addExitZone({ side: 'bottom', toScene: SCENE.HOME, label: '{arrow_down} Dom', data: homeArrival('kuchnia') });

    new PlacementHud(this);
    new MoneyBar(this);
    attachFriendsButton(this);

    this._unsub = gameState.on(() => {
      this._refreshSubA();
      this.natalka?.setOutfit(gameState.raw?.inventory?.equipped);
    });
    this.events.on('shutdown', () => this._unsub?.());

    attachCoordsDebug(this);
    if (import.meta.env?.DEV) attachSceneEditMode(this);  // dev-only foreground/walk authoring (press E)

    if (window.__myId) this._presence = attachRemotePresence(this, `house:${window.__myId}:${this.scene.key}`);
    if (window.__myId) attachRoomChat(this, `house:${window.__myId}:${this.scene.key}`);
  }

  _renderPlacementsAndAnimals() {
    const placements = (gameState.raw?.placements ?? []).filter(p => p.scene === SCENE_KEY);
    for (const p of placements) {
      const tex = placementTexture(p);
      const sprite = this.add.image(p.x, p.y, this.textures.exists(tex) ? tex : 'plot_empty')
        .setInteractive({ useHandCursor: true });
      if (!this.textures.exists(tex)) preloader.whenReady(tex, () => { if (sprite.active) { sprite.setTexture(tex); sizePlacement(sprite, p, this.perspective); } });
      sizePlacement(sprite, p, this.perspective);
      applyPlacementFlip(sprite, p);
      attachClockHand(this, sprite, p);
      attachPlacementMenu(this, sprite, p);
      sprite.placement = p;
      this._placementSprites.set(p.id, sprite);
      if (p.type === 'buda') this.animals.attachBudaClick(sprite, p);
    }
  }

  _refreshSubA() {
    for (const sp of this._placementSprites.values()) sp.destroy();
    this._placementSprites.clear();
    this._renderPlacementsAndAnimals();
  }

  update(_t, dt) {
    this.walker.update(dt);
    fitCharacter(this);
    this.animals.update(dt);
    depthSortScene(this, { actor: this.natalka, placements: this._placementSprites.values(), presence: this._presence });
    if (!this.furnitureSeats?.isSeated) this.transition.checkExits(this.natalka);
    // Seated, her (x, y) is the pose anchor on the piece: save where standing up puts her.
    const at = this.furnitureSeats?.standingPoint() ?? this.natalka;
    reportLocation(this, at.x, at.y);
  }

  async _onAnimalClick(a, opts = {}) {
    return openAnimalActions(this, a, { ...opts, onPetHeart: () => this.animals.sprites.get(a.id)?.showPetHeart() });
  }
}
